import IconHelp from "assets/img/icon-help.svg?react"

import React, { FunctionComponent, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { ArticleAny } from "@/types"
import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import Modal from "components/Modal/Modal"
import Button from "components/Button/Button"
import Asset, { AssetOwnershipType } from "models/Asset"
import { AssetStoredStatus as AssetStoredStatusEnum } from "models/AssetDisplayStatus"
import CopyAssetHeader from "./CopyAssetHeader"
import CopyAssetSharedDefaults from "./CopyAssetSharedDefaults"
import CopyAssetRowList from "./CopyAssetRowList"
import CopyAssetRowCard from "./CopyAssetRowCard"
import CopyAssetHelpModal from "./CopyAssetHelpModal"
import CopyAssetWarningModal from "./CopyAssetWarningModal"
import CopyAssetResults from "./CopyAssetResults"
import useCopyAssetRows from "./useCopyAssetRows"
import useCopyAssetCreation, { CopyAssetCreationStatus } from "./useCopyAssetCreation"
import { getCopyAssetSourceKind } from "./copyAssetMatrix"
import { LmidModalMode } from "components/LmidModal/lmidModalMode"
import { Currency } from "config/constants"
import { useTenantSettings } from "stores/tenantSettings"
import { ICopyAssetSourceValues, rowErrors } from "./copyAssetRows"
import "./CopyAsset.scss"

export interface ICopyAssetModalProps {
    asset: Asset
    article: ArticleAny
    ownershipType: AssetOwnershipType
    /** Initial condition chosen for the copies in the ownership step. */
    isSecondhand: boolean
    storedStatus?: AssetStoredStatusEnum | null
    onClose(): void
}

/** Source values the copy rows are prefilled from. */
function toSourceValues(asset: Asset): ICopyAssetSourceValues {
    return {
        status: asset.status,
        internalOrderNumber: asset.internalOrderNumber,
        supplierOrderNo: asset.supplierOrderNo,
        constructionYear: asset.constructionYear,
        referenceNumberSale: asset.assetFinancial?.referenceNumberSale,
        referenceNumberRental: asset.assetFinancial?.referenceNumberRental,
        internalNotes: asset.internalNotes,
        serialNumber: asset.serialNo,
        serialNumberMachine: asset.serialNoMachine,
        inventoryNo: asset.inventoryNo,
        monthlyRentalCostValue: asset.assetFinancial?.monthlyRentalCost?.rentalCost?.amount != null
            ? String(asset.assetFinancial.monthlyRentalCost.rentalCost.amount)
            : "",
        referenceValue: asset.assetFinancial?.referenceValue?.amount != null
            ? String(asset.assetFinancial.referenceValue.amount)
            : "",
    }
}

/**
 * The copy dialog: a master list of copies on the left, one editable card per
 * copy on the right, and the shared "applies to all copies" values on top.
 * The footer summarises how many copies will be created from the source and
 * blocks creation while any copy still needs input.
 */
const CopyAssetModal: FunctionComponent<React.PropsWithChildren<ICopyAssetModalProps>> = ({
    asset,
    article,
    ownershipType,
    isSecondhand,
    storedStatus,
    onClose,
}) => {
    const { t } = useTranslation()
    const [showHelp, setShowHelp] = useState(false)
    const [showWarning, setShowWarning] = useState(false)
    const cardsRef = useRef<HTMLDivElement>(null)
    const programmaticScrollTargetRef = useRef<number | null>(null)
    const scrollDetectPendingRef = useRef(false)
    const { tenantSettings } = useTenantSettings()

    const sourceValues = toSourceValues(asset)
    const defaultCurrency = tenantSettings?.defaultCurrency ?? Currency.EUR
    const amountCurrencies = {
        monthlyRentalCostValue: asset.assetFinancial?.monthlyRentalCost?.rentalCost?.currency || defaultCurrency,
        referenceValue: asset.assetFinancial?.referenceValue?.currency || defaultCurrency,
    }

    const {
        state,
        readyCount,
        editedCount,
        firstInvalidId,
        canAdd,
        canRemove,
        selectRow,
        addRow,
        duplicateRow,
        removeRow,
        setRowValue,
        setRowOverride,
        setShared,
        setLmidMode,
        assignLmid,
    } = useCopyAssetRows({
        sourceKind: getCopyAssetSourceKind(article.articleType, article.isExtManufacturer),
        ownershipType,
        sourceOwnershipType: asset.ownershipType,
        isSecondhand,
        source: sourceValues,
    })

    const { creationState, run, retry } = useCopyAssetCreation({
        source: asset,
        isExtManufacturer: article.isExtManufacturer,
        defaultCurrency,
    })

    const total = state.rows.length
    const missingCount = total - readyCount
    const successCount = Object.values(creationState.resultsByRowId)
        .filter(result => result.status === CopyAssetCreationStatus.success).length
    const allSettled = !creationState.isRunning && state.rows.every(row => {
        const status = creationState.resultsByRowId[row.id]?.status
        return status === CopyAssetCreationStatus.success || status === CopyAssetCreationStatus.failed
    })
    const canClose = !creationState.hasStarted || allSettled

    function selectAndScrollTo(id: string) {
        selectRow(id)
        const container = cardsRef.current
        const card = container?.querySelector<HTMLElement>(`[data-row-id="${id}"]`)
        if (!container || !card) return
        const target = Math.min(Math.max(0, card.offsetTop), container.scrollHeight - container.clientHeight)
        programmaticScrollTargetRef.current = target
        if (typeof container.scrollTo === "function") {
            container.scrollTo({ top: target, behavior: "smooth" })
        } else {
            container.scrollTop = target
        }
    }

    function detectCurrentCard() {
        const container = cardsRef.current
        if (!container || creationState.hasStarted) return

        if (programmaticScrollTargetRef.current !== null) {
            if (Math.abs(container.scrollTop - programmaticScrollTargetRef.current) < 2) {
                programmaticScrollTargetRef.current = null
            }
            return
        }

        let currentId: string | null = null
        let currentDistance = Infinity
        container.querySelectorAll<HTMLElement>("[data-row-id]").forEach(anchor => {
            const distance = Math.abs(anchor.offsetTop - container.scrollTop)
            if (distance < currentDistance) {
                currentDistance = distance
                currentId = anchor.dataset.rowId ?? null
            }
        })

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
            currentId = state.rows[state.rows.length - 1].id
        }

        if (currentId && currentId !== state.activeRowId) {
            selectRow(currentId)
        }
    }

    function handleCardsScroll() {
        if (scrollDetectPendingRef.current) return
        scrollDetectPendingRef.current = true
        requestAnimationFrame(() => {
            scrollDetectPendingRef.current = false
            detectCurrentCard()
        })
    }

    function takenLmidCopyNumbersFor(ownRowId: string): Record<string, number> {
        const taken: Record<string, number> = {}
        for (const [index, row] of state.rows.entries()) {
            if (row.id !== ownRowId && row.lmidMode === LmidModalMode.enterLmid && row.lmid.trim()) {
                taken[row.lmid.trim()] ??= index + 1
            }
        }
        return taken
    }

    return (
        <>
            <Modal
                title={t('copyAsset.copyAsset')}
                className="copy-asset-modal"
                onClose={canClose ? onClose : undefined}
                closeButton
                maxWidth="min(180rem, 98vw)"
                fullHeight
                modalContentStyle={{ overflow: "hidden", display: "flex", flexDirection: "column" }}
                toolbar={
                    <Flex alignItems="center" gap="2.4rem" className="w-100">
                        <CopyAssetHeader
                            asset={asset}
                            article={article}
                            ownershipType={ownershipType}
                            isSecondhand={isSecondhand}
                            storedStatus={storedStatus}
                        />
                        <Button
                            round
                            theme="grey"
                            ariaLabel={t('copyAsset.help.title')}
                            title={t('copyAsset.help.title')}
                            icon={<IconHelp />}
                            onClick={() => setShowHelp(true)}
                        />
                    </Flex>
                }
                footer={
                    creationState.hasStarted
                        ? <Flex justifyContent="space-between" alignItems="center" gap="2.4rem" className="w-100">
                            <Text type="p" data-testid="copy-asset-summary">
                                {t('copyAsset.copiesCreated', { count: successCount, lmid: asset.lmid })}
                            </Text>
                            <Button
                                label={t('common.ok')}
                                disabled={!allSettled}
                                onClick={onClose}
                                data-testid="copy-asset-ok"
                            />
                        </Flex>
                        : <Flex justifyContent="space-between" alignItems="center" gap="2.4rem" className="w-100">
                            <Flex alignItems="center" gap="1.2rem">
                                <Text type="p" data-testid="copy-asset-summary">
                                    {missingCount > 0
                                        ? t('copyAsset.needsRequiredField', { count: missingCount })
                                        : t('copyAsset.willBeCreated', { count: total, lmid: asset.lmid })}
                                </Text>
                                <button
                                    type="button"
                                    className="copy-asset__ready-indicator"
                                    title={t('copyAsset.jumpToIncomplete')}
                                    disabled={!firstInvalidId}
                                    onClick={() => firstInvalidId && selectAndScrollTo(firstInvalidId)}
                                    data-testid="copy-asset-ready-indicator">
                                    <Text type="pSmallest" className="color-grey-500">
                                        {t('copyAsset.readyCount', { ready: readyCount, total })}
                                    </Text>
                                    <span className="copy-asset__progress">
                                        <span
                                            className={missingCount ? "copy-asset__progress-bar copy-asset__progress-bar--partial" : "copy-asset__progress-bar"}
                                            style={{ width: `${Math.round((readyCount / total) * 100)}%` }}
                                        />
                                    </span>
                                </button>
                            </Flex>
                            <Flex gap="1.2rem">
                                <Button
                                    theme="secondary"
                                    label={t('common.cancel')}
                                    onClick={onClose}
                                />
                                <Button
                                    label={t('copyAsset.createCopies', { count: total })}
                                    disabled={missingCount > 0}
                                    onClick={() => missingCount === 0 && setShowWarning(true)}
                                    data-testid="copy-asset-create"
                                />
                            </Flex>
                        </Flex>
                }>
                {creationState.hasStarted &&
                    <CopyAssetResults
                        state={state}
                        sourceLmid={asset.lmid}
                        sourceAssetId={asset.id}
                        sourceValues={sourceValues}
                        amountCurrencies={amountCurrencies}
                        resultsByRowId={creationState.resultsByRowId}
                        isRunning={creationState.isRunning}
                        onRetry={rowId => retry(state, rowId)}
                    />
                }
                {!creationState.hasStarted && <>
                <CopyAssetSharedDefaults
                    state={state}
                    editedCount={editedCount}
                    amountCurrencies={amountCurrencies}
                    onSetShared={setShared}
                />
                <div className="copy-asset__master-detail">
                    <CopyAssetRowList
                        state={state}
                        canAdd={canAdd}
                        onSelect={selectAndScrollTo}
                        onAdd={addRow}
                    />
                    <div
                        className="copy-asset__cards border-grey-100 bg-white"
                        ref={cardsRef}
                        onScroll={handleCardsScroll}
                        onWheel={() => { programmaticScrollTargetRef.current = null }}>
                        {state.rows.map((row, index) => {
                            const takenLmidCopyNumbers = takenLmidCopyNumbersFor(row.id)
                            return (
                                <div key={row.id} data-row-id={row.id} className="copy-asset__card-anchor">
                                    <CopyAssetRowCard
                                        state={state}
                                        row={row}
                                        rowNumber={index + 1}
                                        errors={rowErrors(state, row)}
                                        isSelected={row.id === state.activeRowId}
                                        canDuplicate={canAdd}
                                        canRemove={canRemove}
                                        articleNumber={article.articleNo ?? ""}
                                        isInternalArticle={!article.isExtManufacturer}
                                        takenLmids={Object.keys(takenLmidCopyNumbers)}
                                        takenLmidCopyNumbers={takenLmidCopyNumbers}
                                        amountCurrencies={amountCurrencies}
                                        onSelect={() => selectRow(row.id)}
                                        onDuplicate={() => duplicateRow(row.id)}
                                        onRemove={() => removeRow(row.id)}
                                        onSetRowValue={(key, value) => setRowValue(row.id, key, value)}
                                        onSetOverride={(key, value) => setRowOverride(row.id, key, value)}
                                        onSetLmidMode={mode => setLmidMode(row.id, mode)}
                                        onAssignLmid={(lmid, createAssetLink, overwrite) =>
                                            assignLmid(row.id, lmid, createAssetLink, overwrite)}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div>
                </>}
            </Modal>
            {showHelp &&
                <CopyAssetHelpModal onClose={() => setShowHelp(false)} />
            }
            {showWarning &&
                <CopyAssetWarningModal
                    count={total}
                    sourceLmid={asset.lmid}
                    onConfirm={() => {
                        setShowWarning(false)
                        run(state)
                    }}
                    onGoBack={() => setShowWarning(false)}
                />
            }
        </>
    )
}

export default CopyAssetModal
